import { useEffect, useState } from "react"
import type { ReactNode } from "react"
import { TabBar } from "./TabBar"
import type { TabKey } from "./TabBar"
import { T } from "./theme"
import type { MobileAsset } from "./types"

interface MobileLayoutProps {
  active: TabKey
  onTabChange: (tab: TabKey) => void
  onOpenScan: () => void
  role: "admin" | "regular"
  children: ReactNode
  /** When set, renders the asset detail screen on top. */
  assetDetail?: ReactNode
  /** When set, renders the scan screen on top. */
  scanScreen?: ReactNode
}

/**
 * Frame that hosts a single screen at a time, with the bottom tab bar and
 * room for full-screen overlays (scan, asset detail).
 */
export function MobileLayout({
  active,
  onTabChange,
  onOpenScan,
  role,
  children,
  assetDetail,
  scanScreen,
}: MobileLayoutProps) {
  // The installed PWA keeps a safe-area gap below the layout viewport; it
  // shows the document background. Match it to the tab bar surface so the gap
  // reads as part of the bar (the scan screen overrides this while open).
  useEffect(() => {
    const el = document.documentElement
    const prev = el.style.background
    el.style.background = T.surface
    return () => {
      el.style.background = prev
    }
  }, [])

  const handleTab = (tab: TabKey) => {
    if (tab === "scan") {
      onOpenScan()
      return
    }
    onTabChange(tab)
  }

  return (
    <div
      className="mobile-shell"
      style={{
        background: T.bg,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          paddingBottom: T.tabBarHeight + 16,
          WebkitOverflowScrolling: "touch",
        }}
      >
        {children}
      </div>
      <TabBar active={active} onChange={handleTab} role={role} />
      {assetDetail}
      {scanScreen}
    </div>
  )
}

/**
 * Convenience hook — keeps tab/scan/asset-detail state for the top-level
 * mobile page. Inertia pages just `useMobileNav()` and pass the parts to the layout.
 */
export function useMobileNav() {
  const [tab, setTab] = useState<TabKey>("home")
  const [scanOpen, setScanOpen] = useState(false)
  const [openAsset, setOpenAsset] = useState<Pick<MobileAsset, "id" | "code"> | null>(null)

  return {
    tab,
    setTab: (t: TabKey) => {
      setTab(t)
      setOpenAsset(null)
    },
    scanOpen,
    openScan: () => setScanOpen(true),
    closeScan: () => setScanOpen(false),
    openAsset,
    setOpenAsset,
  }
}
